import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { chatKeys, useMisConversacionIds, useConversaciones } from '../hooks/useChat'
import { chatService } from '../services/chatService'
import { playNotificationSound } from '@/lib/notification-sound'
import type { Mensaje, ConnectionStatus, TypingEvent } from '../types/chat'
import type { RealtimeChannel } from '@supabase/supabase-js'

// ===========================================
// Context
// ===========================================

interface ChatContextValue {
  conversacionActiva: string | null
  connectionStatus: ConnectionStatus
  onlineUsers: string[]
  chatOpen: boolean
  unreadCount: number
  abrirConversacion: (convId: string) => void
  cerrarConversacion: () => void
  toggleChat: () => void
  setChatOpen: (open: boolean) => void
  isOnline: (email: string) => boolean
  sendBroadcast: (mensaje: Mensaje) => void
  sendTyping: (conversacionId: string, isTyping: boolean) => void
  getTypingUsers: (conversacionId: string) => string[]
  setMutedConversations: (ids: Set<string>) => void
  sendReadReceipt: (conversacionId: string, messageId: string) => void
  getReadBy: (conversacionId: string, messageId: string) => string[]
  sendMemberChange: (conversacionId: string) => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export function useChatContext() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext debe usarse dentro de ChatProvider')
  }
  return context
}

const canalTokenKey = (orgId: string) => ['chat', 'canal', orgId] as const

// ===========================================
// Provider
// ===========================================

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth0()
  const { supabase } = useSupabase()
  const { membresia } = useOrganizacion()
  const queryClient = useQueryClient()

  const orgId = membresia?.organizacion_id ?? ''
  const userEmail = user?.email ?? ''

  // El nombre de los canales lleva un token que solo se entrega a miembros activos.
  // Sin token no se abre ningún canal.
  const { data: canalToken } = useQuery({
    queryKey: canalTokenKey(orgId),
    queryFn: async () => {
      const { data, error } = await supabase!.rpc('chat_canal_token' as never, {
        p_organizacion_id: orgId,
      } as never)
      if (error) throw error
      return (data as string | null) ?? null
    },
    enabled: !!supabase && isAuthenticated && !!orgId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  })
  const canal = canalToken ?? ''

  // State
  const [conversacionActiva, setConversacionActiva] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const [onlineUsers, setOnlineUsers] = useState<string[]>([])
  const [chatOpen, setChatOpen] = useState(false)
  const [typingUsers, setTypingUsers] = useState<Map<string, Set<string>>>(new Map())

  // Unread count derivado de conversaciones.
  // Single source of truth: la BD via RPC chat_unread_counts.
  // Esto arregla dos bugs a la vez:
  //   - Bug 2: badge mostraba 0 al cargar porque el state local no se hidrataba
  //   - Bug 1c: setUnreadCount(0) optimista escondía el bug de mark-as-read
  // useConversaciones se monta con el provider → hidrata desde BD al arrancar la app.
  const { data: conversaciones = [] } = useConversaciones()
  const unreadCount = useMemo(
    () => conversaciones.reduce((sum, c) => sum + (c.no_leidos ?? 0), 0),
    [conversaciones]
  )
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const mutedConvIdsRef = useRef<Set<string>>(new Set())
  // Map: conversacionId → Map<userEmail, lastReadMessageId>
  const [readReceipts, setReadReceipts] = useState<Map<string, Map<string, string>>>(new Map())

  // Membership: Set of conversation IDs where user is a member
  const { data: myConvIds } = useMisConversacionIds()
  const myConvIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (myConvIds) myConvIdsRef.current = myConvIds
  }, [myConvIds])

  // Refs for channels and state access inside callbacks
  const presenceChannelRef = useRef<RealtimeChannel | null>(null)
  const globalChannelRef = useRef<RealtimeChannel | null>(null)
  const conversacionActivaRef = useRef<string | null>(null)
  // Último token con el que el canal global llegó a SUBSCRIBED.
  const ultimoCanalSuscriptoRef = useRef<string | null>(null)
  // M1: debounce de invalidaciones para evitar N re-renders en ráfagas de mensajes
  const invalidateConvsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // M4: sincronizar ref en useEffect en vez de mutarla durante el render
  // (mutar refs en render es anti-patrón, puede fallar con Concurrent Mode).
  useEffect(() => {
    conversacionActivaRef.current = conversacionActiva
  }, [conversacionActiva])

  // ===========================================
  // Presence: online/offline tracking
  // ===========================================

  useEffect(() => {
    if (!supabase || !isAuthenticated || !userEmail || !canal) return

    const channel = supabase.channel(`presence:canal:${canal}`)

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState()
        const emails = Object.values(state)
          .flat()
          .map((p: Record<string, unknown>) => String(p.email))
        setOnlineUsers([...new Set(emails)])
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          try {
            await channel.track({ email: userEmail, online_at: new Date().toISOString() })
          } catch {
            setTimeout(() => {
              channel.track({ email: userEmail, online_at: new Date().toISOString() }).catch(() => {})
            }, 2000)
          }
        }
      })

    presenceChannelRef.current = channel

    return () => {
      channel.unsubscribe()
      supabase.removeChannel(channel)
      presenceChannelRef.current = null
      setOnlineUsers([])
    }
  }, [supabase, isAuthenticated, userEmail, canal])

  // ===========================================
  // Global: Shared Broadcast channel per organization
  // All org users subscribe to same channel → messages reach everyone
  // (postgres_changes fails with Auth0 JWT — missing root-level 'role')
  // ===========================================

  useEffect(() => {
    if (!supabase || !isAuthenticated || !userEmail || !canal) return

    // Shared channel: every member of the org subscribes to the channel named by its token
    const channel = supabase.channel(`chat:canal:${canal}`)

    channel
      .on('broadcast', { event: 'new_message' }, async (payload) => {
        // El canal es PÚBLICO, así que por acá no viaja el texto: solo el aviso de que algo
        // pasó. El contenido se trae por PostgREST, donde hay sesión de Auth0 y RLS por
        // membresía. Mismo patrón que ORR-252 en notificaciones.
        const aviso = payload.payload as Pick<Mensaje, 'id' | 'conversacion_id' | 'sender_email'>

        if (!aviso?.id) return

        // Skip own messages (already in cache via optimistic update)
        if (aviso.sender_email === userEmail) return

        // Filtro de membresía: ya NO es la barrera de seguridad —eso ahora lo hace la RLS—,
        // queda como atajo para no ir a buscar un mensaje que no nos corresponde.
        if (!myConvIdsRef.current.has(aviso.conversacion_id)) return

        // Si RLS lo niega, `mensaje` viene null y no hay nada que hacer: no somos miembros.
        let mensaje: Mensaje | null = null
        try {
          mensaje = await chatService.obtenerMensajePorId(supabase, aviso.id)
        } catch (error) {
          console.error('Error trayendo el mensaje anunciado por el canal:', error)
          return
        }
        if (!mensaje) return

        const activeConv = conversacionActivaRef.current

        // Add to active conversation cache (with dedup)
        if (mensaje.conversacion_id === activeConv) {
          queryClient.setQueryData<Mensaje[]>(
            chatKeys.mensajes(mensaje.conversacion_id),
            (old) => {
              if (!old) return [mensaje]
              if (old.some((m) => m.id === mensaje.id)) return old
              return [mensaje, ...old]
            }
          )
        }

        // Sound only for non-muted conversations
        if (typeof document !== 'undefined' && !mutedConvIdsRef.current.has(mensaje.conversacion_id)) {
          playNotificationSound()
        }

        // M1: debounce de invalidación. En ráfagas (10 mensajes en 1s) se dispara
        // 1 sola invalidación al final en vez de 10. Refrescar el list trigger el RPC
        // chat_unread_counts que recalcula y el unreadCount derivado se actualiza.
        if (invalidateConvsTimerRef.current) clearTimeout(invalidateConvsTimerRef.current)
        invalidateConvsTimerRef.current = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
        }, 300)
      })
      .on('broadcast', { event: 'typing' }, (payload) => {
        const { conversacion_id, user_email, is_typing } = payload.payload as TypingEvent

        if (user_email === userEmail) return
        if (!myConvIdsRef.current.has(conversacion_id)) return

        setTypingUsers((prev) => {
          const next = new Map(prev)
          const users = new Set(next.get(conversacion_id) ?? [])

          if (is_typing) {
            users.add(user_email)
          } else {
            users.delete(user_email)
          }

          if (users.size === 0) next.delete(conversacion_id)
          else next.set(conversacion_id, users)
          return next
        })

        // Auto-clear after 5s (safety net if stop event is lost)
        const timeoutKey = `${conversacion_id}:${user_email}`
        const existing = typingTimeoutsRef.current.get(timeoutKey)
        if (existing) clearTimeout(existing)

        if (is_typing) {
          typingTimeoutsRef.current.set(
            timeoutKey,
            setTimeout(() => {
              setTypingUsers((prev) => {
                const next = new Map(prev)
                const users = new Set(next.get(conversacion_id) ?? [])
                users.delete(user_email)
                if (users.size === 0) next.delete(conversacion_id)
                else next.set(conversacion_id, users)
                return next
              })
              typingTimeoutsRef.current.delete(timeoutKey)
            }, 5000)
          )
        } else {
          typingTimeoutsRef.current.delete(timeoutKey)
        }
      })
      .on('broadcast', { event: 'read_receipt' }, (payload) => {
        const { conversacion_id, user_email, last_read_message_id } = payload.payload as {
          conversacion_id: string
          user_email: string
          last_read_message_id: string
        }

        if (user_email === userEmail) return
        if (!myConvIdsRef.current.has(conversacion_id)) return

        setReadReceipts((prev) => {
          const next = new Map(prev)
          const convReceipts = new Map(next.get(conversacion_id) ?? [])
          convReceipts.set(user_email, last_read_message_id)
          next.set(conversacion_id, convReceipts)
          return next
        })
      })
      .on('broadcast', { event: 'member_change' }, (payload) => {
        const { conversacion_id } = payload.payload as { conversacion_id: string }
        if (myConvIdsRef.current.has(conversacion_id)) {
          queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'members', conversacion_id] })
          queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
        }
        queryClient.invalidateQueries({ queryKey: chatKeys.misConversacionIds(userEmail) })
      })
      .on('broadcast', { event: 'canal_rotado' }, () => {
        // El token actual queda revocado: se descarta antes de pedir el nuevo, así los canales
        // se cierran y no se envía nada aunque la RPC falle.
        queryClient.setQueryData(canalTokenKey(orgId), null)
        queryClient.invalidateQueries({ queryKey: canalTokenKey(orgId) })
      })
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          setConnectionStatus('connected')
          // Al pasar a un token nuevo se pudieron perder avisos publicados antes de suscribirse.
          const anterior = ultimoCanalSuscriptoRef.current
          ultimoCanalSuscriptoRef.current = canal
          if (anterior && anterior !== canal) {
            const activa = conversacionActivaRef.current
            if (activa) queryClient.invalidateQueries({ queryKey: chatKeys.mensajes(activa) })
            queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
          }
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Chat broadcast channel error:', err)
          setConnectionStatus('reconnecting')
        } else if (status === 'TIMED_OUT') {
          setConnectionStatus('reconnecting')
        }
      })

    globalChannelRef.current = channel

    return () => {
      channel.unsubscribe()
      supabase.removeChannel(channel)
      globalChannelRef.current = null
      setConnectionStatus('disconnected')
      if (invalidateConvsTimerRef.current) {
        clearTimeout(invalidateConvsTimerRef.current)
        invalidateConvsTimerRef.current = null
      }
    }
  }, [supabase, isAuthenticated, userEmail, orgId, canal, queryClient])

  // Per-conversation channel removed — org channel handles all delivery.

  // ===========================================
  // Actions
  // ===========================================

  const abrirConversacion = useCallback((convId: string) => {
    setConversacionActiva(convId)
    setChatOpen(true)
    // NO setUnreadCount(0) optimista: el contador se actualiza cuando
    // la mutation marcarLeido (en ChatWindow) invalida chatKeys.conversaciones
    // → refetch del RPC → useMemo recalcula unreadCount derivado.
    queryClient.invalidateQueries({ queryKey: chatKeys.mensajes(convId) })
    // Add to membership cache immediately (for new conversations)
    myConvIdsRef.current.add(convId)
  }, [queryClient])

  const cerrarConversacion = useCallback(() => {
    setConversacionActiva(null)
  }, [])

  const toggleChat = useCallback(() => {
    setChatOpen((prev) => !prev)
  }, [])

  const isOnline = useCallback(
    (email: string) => onlineUsers.includes(email),
    [onlineUsers]
  )

  // Broadcast to shared org channel → ALL org users receive it
  const sendBroadcast = useCallback(
    (mensaje: Mensaje) => {
      if (mensaje.sender_email !== userEmail) return
      // Send to shared org channel (all org members are subscribed)
      if (globalChannelRef.current) {
        // Solo el aviso. El texto del mensaje NO sale por el canal: es público y cualquiera con
        // la anon key —que va en el bundle— podría leerlo sin estar autenticado.
        globalChannelRef.current.send({
          type: 'broadcast',
          event: 'new_message',
          payload: {
            id: mensaje.id,
            conversacion_id: mensaje.conversacion_id,
            sender_email: mensaje.sender_email,
          },
        })
      }
    },
    [userEmail]
  )

  const sendTyping = useCallback(
    (conversacionId: string, isTyping: boolean) => {
      if (!globalChannelRef.current) return
      globalChannelRef.current.send({
        type: 'broadcast',
        event: 'typing',
        payload: { conversacion_id: conversacionId, user_email: userEmail, is_typing: isTyping },
      })
    },
    [userEmail]
  )

  const getTypingUsers = useCallback(
    (conversacionId: string): string[] => {
      return [...(typingUsers.get(conversacionId) ?? [])]
    },
    [typingUsers]
  )

  const setMutedConversations = useCallback((ids: Set<string>) => {
    mutedConvIdsRef.current = ids
  }, [])

  const sendReadReceipt = useCallback(
    (conversacionId: string, messageId: string) => {
      if (!globalChannelRef.current) return
      globalChannelRef.current.send({
        type: 'broadcast',
        event: 'read_receipt',
        payload: { conversacion_id: conversacionId, user_email: userEmail, last_read_message_id: messageId },
      })
    },
    [userEmail]
  )

  const sendMemberChange = useCallback(
    (conversacionId: string) => {
      if (!globalChannelRef.current) return
      globalChannelRef.current.send({
        type: 'broadcast',
        event: 'member_change',
        payload: { conversacion_id: conversacionId },
      })
    },
    []
  )

  const getReadBy = useCallback(
    (conversacionId: string, messageId: string): string[] => {
      const convReceipts = readReceipts.get(conversacionId)
      if (!convReceipts) return []
      return [...convReceipts.entries()]
        .filter(([, lastRead]) => lastRead >= messageId)
        .map(([email]) => email)
    },
    [readReceipts]
  )

  // ===========================================
  // Value
  // ===========================================

  const value: ChatContextValue = {
    conversacionActiva,
    connectionStatus,
    onlineUsers,
    chatOpen,
    unreadCount,
    abrirConversacion,
    cerrarConversacion,
    toggleChat,
    setChatOpen,
    isOnline,
    sendBroadcast,
    sendTyping,
    getTypingUsers,
    setMutedConversations,
    sendReadReceipt,
    getReadBy,
    sendMemberChange,
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}
